import { memo, useMemo, useRef, useState } from 'react';
import { Form, Modal, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import { CustomButton } from '../../../../../components';
import type {
  SchedulePrintLayout,
  SchedulePrintMapBuildOptions,
  SchedulePrintModel,
} from './schedulePrintModel';
import { courtRangesOverlap } from './schedulePrintModel';
import { SchedulePrintCourtRangePicker } from './SchedulePrintCourtRangePicker';
import { SchedulePrintCategoryPicker, type SchedulePrintCategoryOption } from './SchedulePrintCategoryPicker';
import { SchedulePrintDocument } from './SchedulePrintDocument';
import { useSchedulePrintMapPrefs } from './useSchedulePrintMapPrefs';
import { useSchedulePrintListPrefs } from './useSchedulePrintListPrefs';

type SchedulePrintPreviewModalProps = {
  show: boolean;
  onClose: () => void;
  buildPrintModel: (mapOptions?: SchedulePrintMapBuildOptions) => SchedulePrintModel | null;
  courtNames: string[];
  categoryOptions: SchedulePrintCategoryOption[];
  documentTitle: string;
};

export const SchedulePrintPreviewModal = memo(function SchedulePrintPreviewModal({
  show,
  onClose,
  buildPrintModel,
  courtNames,
  categoryOptions,
  documentTitle,
}: SchedulePrintPreviewModalProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const printRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<SchedulePrintLayout>('map');
  const { mapOrientation, setMapOrientation, courtRanges, setCourtRanges } =
    useSchedulePrintMapPrefs();
  const { listCategoryIds, setListCategoryIds } = useSchedulePrintListPrefs();

  const model = useMemo(
    () =>
      show
        ? buildPrintModel({
            orientation: mapOrientation,
            courtRanges,
            listCategoryIds: layout === 'list' ? listCategoryIds : null,
          })
        : null,
    [buildPrintModel, courtRanges, layout, listCategoryIds, mapOrientation, show]
  );

  const hasScheduleData = model != null;
  const hasPrintableContent =
    layout === 'list'
      ? (model?.matchCount ?? 0) > 0
      : (model?.mapDays.some((day) => day.blocks.length > 0) ?? false);
  const showCategoryFilterEmpty =
    layout === 'list' &&
    Boolean(listCategoryIds?.length) &&
    (model?.matchCount ?? 0) === 0;
  const rangesOverlap =
    layout === 'map' &&
    courtRanges != null &&
    courtRanges.length > 1 &&
    courtRangesOverlap(courtRanges);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle,
    bodyClass: 'schedule-print-active',
  });

  const printButton = useMemo(
    () => (
      <CustomButton
        btnText={t(prefix + 'schedulePrintConfirm')}
        variant="primary"
        disabled={!hasPrintableContent || rangesOverlap}
        cb={() => {
          if (hasPrintableContent && !rangesOverlap) handlePrint();
        }}
      />
    ),
    [handlePrint, hasPrintableContent, prefix, rangesOverlap, t]
  );

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="xl"
      centered
      className="schedule-print-preview-modal"
      dialogClassName="schedule-print-preview-modal__dialog"
      contentClassName="schedule-print-preview-modal__content"
    >
      <Modal.Header closeButton>
        <Modal.Title>{t(prefix + 'schedulePrintPreviewTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!hasScheduleData ? (
          <p className="text-muted mb-0">{t(prefix + 'schedulePrintEmpty')}</p>
        ) : (
          <>
            <div className="schedule-print-preview-modal__layout-toggle mb-3">
              <ToggleButtonGroup
                type="radio"
                name="schedule-print-layout"
                value={layout}
                onChange={(value) => setLayout(value as SchedulePrintLayout)}
              >
                <ToggleButton id="schedule-print-layout-map" value="map" variant="outline-secondary">
                  {t(prefix + 'schedulePrintLayoutMap')}
                </ToggleButton>
                <ToggleButton id="schedule-print-layout-list" value="list" variant="outline-secondary">
                  {t(prefix + 'schedulePrintLayoutList')}
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            {layout === 'map' ? (
              <>
                <fieldset className="schedule-print-preview-modal__orientation mb-3">
                  <legend className="form-label fw-semibold mb-2">
                    {t(prefix + 'schedulePrintMapOrientationTitle')}
                  </legend>
                  <Form.Check
                    type="radio"
                    id="schedule-print-orientation-time-on-x"
                    name="schedule-print-map-orientation"
                    label={t(prefix + 'schedulePrintMapOrientationTimeOnX')}
                    checked={mapOrientation === 'timeOnX'}
                    onChange={() => setMapOrientation('timeOnX')}
                  />
                  <Form.Check
                    type="radio"
                    id="schedule-print-orientation-time-on-y"
                    name="schedule-print-map-orientation"
                    label={t(prefix + 'schedulePrintMapOrientationTimeOnY')}
                    checked={mapOrientation === 'timeOnY'}
                    onChange={() => setMapOrientation('timeOnY')}
                  />
                </fieldset>
                <SchedulePrintCourtRangePicker
                  courtNames={courtNames}
                  ranges={courtRanges}
                  onChange={setCourtRanges}
                />
              </>
            ) : (
              <SchedulePrintCategoryPicker
                categoryOptions={categoryOptions}
                listCategoryIds={listCategoryIds}
                onChange={setListCategoryIds}
              />
            )}
            <div className="schedule-print-preview-modal__viewport">
              {hasPrintableContent ? (
                <SchedulePrintDocument ref={printRef} model={model} layout={layout} />
              ) : (
                <p className="text-muted mb-0">
                  {t(
                    prefix +
                      (showCategoryFilterEmpty
                        ? 'schedulePrintListCategoryEmpty'
                        : 'schedulePrintEmpty')
                  )}
                </p>
              )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <CustomButton
          btnText={t(prefix + 'schedulePrintClose')}
          variant="outline-secondary"
          cb={onClose}
        />
        {printButton}
      </Modal.Footer>
    </Modal>
  );
});
